/**
 * The wagering a manual bonus grant owes, derived from the campaign's own term.
 *
 * Both inputs are already in the member's minor units, so this is a scaling of
 * integers and nothing about it may become a float: `Number('0.29') * 3000` is
 * 869.9999999999999, and `Math.floor` of that files 869 — a requirement one
 * minor unit below the term the campaign states, on the figure a player's
 * withdrawals are held against (CLAUDE.md 2.2).
 */
import { parseScaledDecimal } from '@/lib/scaled-decimal';

/**
 * Fractional digits of a multiplier carried into the arithmetic. Six is the
 * scale the house-account rates use, and far past anything a campaign states
 * (`3.00`, `0.35`); a seventh digit is truncated toward zero (§2.5) rather than
 * discarding a term that is otherwise readable.
 */
const MULTIPLIER_SCALE = 6;
const MULTIPLIER_FACTOR = 10n ** BigInt(MULTIPLIER_SCALE);

const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);

/**
 * `amountMinor` times `multiplier`, truncated toward zero, in the same minor
 * units it was given — currency-agnostic for that reason, so a 0-decimal brand
 * needs no separate path.
 *
 * A multiplier that cannot be read at all — blank, negative, not a number — is
 * treated as one times the grant, which is what the screen has always defaulted
 * to for a campaign that states none. It must never become zero (a bonus minted
 * with no wagering at all, withdrawable on landing) and must never become `NaN`,
 * which is what `Number('not a number')` used to put on the wire.
 */
export function turnoverForGrant(
  amountMinor: number,
  multiplier: string | null | undefined,
): number {
  if (!Number.isFinite(amountMinor)) return 0;
  const parsed = parseScaledDecimal(multiplier, MULTIPLIER_SCALE);
  const scaled = parsed === null ? MULTIPLIER_FACTOR : parsed.scaled;
  const required = (BigInt(Math.trunc(amountMinor)) * scaled) / MULTIPLIER_FACTOR;
  // The form's state is a JS number, so a product past 2^53 has to land
  // somewhere; the largest integer a number carries exactly is still an absurd
  // requirement on screen, and it does not silently under-lock the grant.
  if (required > MAX_SAFE) return Number.MAX_SAFE_INTEGER;
  if (required < -MAX_SAFE) return -Number.MAX_SAFE_INTEGER;
  return Number(required);
}
